import {ContactOption} from "../models/enums/ContactOption";
import Interaction from "../models/Interaction";
import React, {useEffect, useRef, useState} from "react";
import {formatDate, formatInteractionTime} from "../utils/DateTimeUtils";
import {getClientById} from "../services/client-service";

function contactOptionIcon(option: ContactOption): string {
    switch (option) {
        case ContactOption.EMAIL:   return '✉️';
        case ContactOption.PHONE:   return '📞';
        case ContactOption.MEETING: return '🤝';
        default:                    return '📋';
    }
}

interface PopupProps {
    interaction: Interaction;
    position: { top: number; left: number };
    onClose: () => void;
    onGoToInteraction: (clientId: number) => void;
}

const InteractionCalendarPopup: React.FC<PopupProps> = ({ interaction, position, onClose, onGoToInteraction }) => {
    const popupRef = useRef<HTMLDivElement>(null);
    const [clientName, setClientName] = useState('');

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        // Small delay so the originating click doesn't immediately close the popup
        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
        }, 50);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    useEffect(() => {
        const getClientName = async () => {
            if (interaction.clientId) {
                try {
                    const response = await getClientById(interaction.clientId);
                    if (response.status === 200 && response.data && response.data.name) {
                        setClientName(response.data.name);   
                    }
                } catch (err: any) {
                    console.error('Failed to fetch client name');
                }
            }
        }
        getClientName();
    }, [interaction.clientId]);

    // Clamp so the popup stays within the viewport
    const POPUP_WIDTH = 300;
    const clampedLeft = Math.min(position.left, window.innerWidth - POPUP_WIDTH - 16);
    const clampedTop = Math.min(position.top, window.innerHeight + window.scrollY - 280);

    return (
        <div
            ref={popupRef}
            className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-4"
            style={{ top: clampedTop, left: clampedLeft, width: POPUP_WIDTH }}
            data-testid='popup-wrapper'

        >
            {/* Header */}
            <div className="flex justify-between items-start mb-3">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-crumbblue">Client Interaction</p>
                    <h3 className="text-base font-bold text-gray-800 leading-tight" data-testid="subject">
                        {interaction.subject}
                    </h3>
                    {clientName && (
                        <p className="text-xs text-gray-500 mt-0.5" data-testid="client-name">
                            {clientName}
                        </p>
                    )}
                </div>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-2 mt-0.5"
                    aria-label="Close"
                    data-testid='close-btn'
                >
                    &times;
                </button>
            </div>

            {/* Details */}
            <div className="space-y-2 text-sm text-gray-700 mb-4">
                <div className="flex items-center gap-2">
                    <span>📅</span>
                    <span data-testid="date">{formatDate(interaction.interactionDate)}</span>
                </div>
                {interaction.interactionTime && (
                    <div className="flex items-center gap-2">
                        <span>🕐</span>
                        <span data-testid='time'>
                            {formatInteractionTime(interaction)}
                        </span>
                    </div>
                )}
                <div className="flex items-center gap-2">
                    <span>{contactOptionIcon(interaction.type)}</span>
                    <span data-testid='contact'>{interaction.type}</span>
                </div>
                <div className="flex items-center gap-2">
                    <span>Outcome: </span>
                    <span data-testid='outcome'>{interaction.outcome}</span>
                </div>
                {interaction.notes && (
                    <div className="flex items-start gap-2 pt-1 border-t border-gray-100">
                        <span className="mt-0.5">📝</span>
                        <span data-testid='note' className="text-gray-600 italic">
                            {interaction.notes.length > 120
                                ? interaction.notes.slice(0, 120) + '...'
                                : interaction.notes
                            }
                        </span>
                    </div>
                )}
            </div>

            {/* CTA */}
            <button
                onClick={() => onGoToInteraction(interaction.id || 0)}
                className="w-full bg-crumbblue text-white text-sm font-medium py-2 rounded hover:bg-blue-700 transition"
                data-testid='view-interaction-btn'
            >
                View Interaction Page →
            </button>
        </div>
    );
};

export default InteractionCalendarPopup;